import { useCallback, useRef, useState } from 'react';
import { environment } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { isInternalStaffEmail } from '../../utils/staffAccess';
import { FeedbackModal } from './FeedbackModal';

/**
 * Header help button + popover (#120), matching Desk's HelpMenu:
 *  - AI Docs: a placeholder, visibly disabled ("Coming soon").
 *  - Send feedback: only when the feedback endpoint is configured for this environment.
 *  - QA Tracker: Meshtek staff only.
 */
export function HelpMenu() {
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(wrapperRef, close, open);

  const feedbackAvailable = Boolean(environment.feedbackApiUrl);
  const showQaTracker = Boolean(environment.qaTrackerUrl) && isInternalStaffEmail(user?.email);

  return (
    <div ref={wrapperRef} className="help-menu">
      <button
        type="button"
        className="btn btn-ghost help-menu-trigger"
        // No aria-haspopup: this is a plain stack of buttons, not an ARIA menu.
        aria-expanded={open}
        aria-label="Help and feedback"
        title="Help"
        onClick={() => setOpen((v) => !v)}
      >
        ?
      </button>

      {open && (
        <div className="help-menu-popover">
          {/* TODO(ai-docs): the real assistant is tracked in meshtek-io/detect#122. */}
          <button type="button" className="help-menu-item" disabled aria-disabled="true">
            <span className="help-menu-item-title">
              AI Docs <span className="help-menu-badge">Coming soon</span>
            </span>
            <span className="help-menu-item-help">Ask a question about how Detect works</span>
          </button>
          {feedbackAvailable && (
            <button
              type="button"
              className="help-menu-item"
              onClick={() => {
                setOpen(false);
                setFeedbackOpen(true);
              }}
            >
              <span className="help-menu-item-title">Send feedback</span>
              <span className="help-menu-item-help">Report a bug, request a feature, or send a comment</span>
            </button>
          )}
          {showQaTracker && (
            <button
              type="button"
              className="help-menu-item"
              onClick={() => {
                window.open(environment.qaTrackerUrl, '_blank', 'noopener,noreferrer');
                setOpen(false);
              }}
            >
              <span className="help-menu-item-title">QA Tracker</span>
              <span className="help-menu-item-help">Open the QA tracker to view and verify test scenarios</span>
            </button>
          )}
        </div>
      )}

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}
